import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import RefreshCountdownSnackbar from "src/components/RefreshCountdownSnackbar";
import { getPanelRefreshSchedule } from "src/api/commonapi";
import {
  REFRESH_POLL_INTERVAL_MS,
  REFRESH_RELOAD_LOCK_MS,
  REFRESH_STORAGE_KEYS,
  REFRESH_TICK_INTERVAL_MS
} from "src/constant/refreshConfig";
import { decidePanelRefresh } from "src/utils/panelRefresh";

// sessionStorage (not localStorage) on purpose: the "already refreshed" markers are
// per-tab. A second tab on the same machine has its own data and must be warned too.
const readSeen = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeSeen = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    // Private mode / disabled storage: the feature degrades to "no suppression",
    // which is safe because the reload lock below still prevents a loop.
  }
};

// Panel Auto-Refresh
//
// Mounted once in DashboardLayout, so it survives every route change and covers the
// whole authenticated panel. Mounted there rather than in App.js so the public pages
// (login, lvb-membership-form, inquiry-form, invoice-download-form) are never touched
// - a reload there would destroy a partially filled long form.
//
// How a refresh is decided
// ------------------------
// Every 15s the server tells us the schedule plus the current clock. We then keep a
// 1s countdown off that server clock, so the timer survives background-tab throttling,
// a sleeping laptop and a user changing their system time - none of which a purely
// client-side setInterval would survive.
//
// Three things can arm the banner, checked in priority order:
// 1. MANUAL    - an admin fired POST /trigger-panel-refresh and this tab has not seen it.
//                 The `active` flag is deliberately NOT checked here: a tab that was
//                 asleep across a 5 minute grace window should still refresh once when
//                 it wakes. sessionStorage is what makes it exactly once per tab.
//   2. SCHEDULED - we are inside the last N seconds before the next boundary.
//   3. MISSED    - the boundary in effect changed since this tab loaded, i.e. the tab
//                  was open (or asleep) across a refresh and its data is now stale.
//
// MISSED is also what makes the "smart detection" work for free: a user who logs in at
// 12:00:10 seeds the current window on first poll, so nothing fires. A user who was
// already on the page across 12:00 has a stale marker and does get warned.
export default function AutoRefreshManager() {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [reason, setReason] = useState(null);

  const scheduleRef = useRef(null);
  const skewRef = useRef(0);
  const seededRef = useRef(false);
  const firedRef = useRef(false);
  // Latched once armed so the countdown cannot drift forward on every poll.
  const pendingRef = useRef(null);

  const doRefresh = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    const pending = pendingRef.current;
    try {
      // Record what we are now fresh for BEFORE reloading. Without this the tab
      // comes back, still sees a different window id, and reloads again.
      // The id is correct for every reason: for a scheduled refresh it is the
      // boundary we are refreshing into, otherwise the window already in effect.
      if (pending) {
        if (pending.windowId) {
          writeSeen(REFRESH_STORAGE_KEYS.SEEN_WINDOW, pending.windowId);
        }
        if (pending.manualId != null) {
          writeSeen(REFRESH_STORAGE_KEYS.SEEN_MANUAL, String(pending.manualId));
        }
      }
      writeSeen(REFRESH_STORAGE_KEYS.RELOAD_LOCK, String(moment().valueOf()));
    } catch (error) {
      // fall through - the reload is still correct
    }
    window.location.reload();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const resp = await getPanelRefreshSchedule();
      if (cancelled || !resp || resp.status !== 1 || !resp.data) return;
      const data = resp.data;
      scheduleRef.current = data;
      skewRef.current = moment(data.server_time).valueOf() - moment().valueOf();

      // First successful poll of this tab. Whatever the payload says is currently
      // true is, by definition, already true for us - the page we just loaded IS the
      // latest data. So record it as handled, which is what stops the banner coming
      // back when the user presses the browser's own reload button.
      //
      // The one exception is a reload we triggered ourselves: a fresh RELOAD_LOCK
      // means doRefresh() already wrote a marker that can be deliberately AHEAD of
      // current_window_id (a scheduled refresh marks the boundary it is refreshing
      // into), and overwriting that would make the tab refresh all over again.
      if (!seededRef.current) {
        seededRef.current = true;
        const selfReload = Number(readSeen(REFRESH_STORAGE_KEYS.RELOAD_LOCK) || 0) > 0;
        if (!selfReload) {
          if (data.current_window_id) {
            writeSeen(REFRESH_STORAGE_KEYS.SEEN_WINDOW, data.current_window_id);
          }
          if (data.manual_refresh && data.manual_refresh.id) {
            writeSeen(REFRESH_STORAGE_KEYS.SEEN_MANUAL, String(data.manual_refresh.id));
          }
          // If this load happened while the 30s warning for the upcoming boundary was
          // already on screen, the load IS the refresh that warning was asking for, so
          // that boundary is handled too. Doing this only inside the window matters:
          // marking the upcoming boundary on every load would suppress the genuine
          // 12:00/16:00/18:00 warning for a tab that opened hours earlier.
          const warningSeconds = data.warning_seconds > 0 ? data.warning_seconds : 30;
          if (
            data.next_refresh_at &&
            data.seconds_until_next > 0 &&
            data.seconds_until_next <= warningSeconds
          ) {
            writeSeen(REFRESH_STORAGE_KEYS.SEEN_WINDOW, data.next_refresh_at);
          }
        }
      }
    };

    poll();
    const id = setInterval(poll, REFRESH_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const evaluate = () => {
      const data = scheduleRef.current;
      if (!data || firedRef.current || !seededRef.current) return;

      const nowMs = moment().valueOf() + skewRef.current;
      if (nowMs - Number(readSeen(REFRESH_STORAGE_KEYS.RELOAD_LOCK) || 0) < REFRESH_RELOAD_LOCK_MS) return;
      if (!data.enabled) return;

      if (pendingRef.current == null) {
        const armed = decidePanelRefresh({
          data,
          nowMs,
          seenWindow: readSeen(REFRESH_STORAGE_KEYS.SEEN_WINDOW),
          seenManual: readSeen(REFRESH_STORAGE_KEYS.SEEN_MANUAL),
          reloadLocked: false
        });
        if (armed == null) return;
        pendingRef.current = armed;
        setReason(armed.armedFor);
      }

      const left = Math.max(0, Math.ceil((pendingRef.current.deadline - nowMs) / 1000));
      setSecondsLeft(left);
      if (left <= 0) doRefresh();
    };

    evaluate();
    const id = setInterval(evaluate, REFRESH_TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [doRefresh]);

  if (reason == null) return null;

  const data = scheduleRef.current || {};
  const copy = data.copy || {};
  const manualBy = (data.manual_refresh && data.manual_refresh.requested_by) || "An admin";

  return (
    <RefreshCountdownSnackbar
      open
      secondsLeft={secondsLeft}
      warningSeconds={data.warning_seconds || 30}
      title={copy[reason + "_title"] || copy.title}
      body={String(copy[reason + "_body"] || copy.body || "").replace("{requested_by}", manualBy)}
      buttonLabel={copy.button || "Refresh Now"}
      onRefresh={doRefresh}
    />
  );
}
